import { useState, useRef, useEffect } from 'react';
import RecordRTC, { StereoAudioRecorder } from 'recordrtc';

const MIN_DURATION_MS = 900;
const MAX_STREAM_AGE  = 10 * 60 * 1000;     // 2-min TTL for the cache
const isIOS           = /iP(ad|hone|od)/.test(navigator.userAgent) &&
                         !/CriOS|FxiOS/.test(navigator.userAgent);

function log(...a) { console.log(...a); }

export default function usePushToTalk() {
  const [supported, setSupported] = useState(false);
  const [recording, setRecording] = useState(false);
  const [starting, setStarting]   = useState(false);

  const recorderRef  = useRef(null);   // RecordRTC instance
  const streamRef    = useRef(null);   // cached "warm" stream
  //const streamTsRef  = useRef(0);      // when it was created
  const keepAliveRef = useRef(null);   // timer for warm window

  const startTimeRef = useRef(null);
  
  /* capability check once */
  useEffect(() => {
    setSupported(!!navigator.mediaDevices?.getUserMedia);
    // Best-effort: if already granted, warm immediately
    // DISABLED - causing reacquisition issues
    // if (navigator.permissions?.query) {
    //  navigator.permissions.query({ name: 'microphone' }).then(p => {
    //    if (p.state === 'granted') getWarmStream().catch(() => {});
    //  }).catch(() => {});
    // }
  }, []);



  /* iOS bug guard: immediately release stream if any audio/video starts playing */
  useEffect(() => {
    const onPlay = () => {
      if (recorderRef.current) return; // don’t break an active recording
      log('🎧 Playback detected → releasing mic stream');
      clearTimeout(keepAliveRef.current);
      safeRelease();
    };
    document.addEventListener('play', onPlay, true); // capture
    return () => document.removeEventListener('play', onPlay, true);
  }, []);

  /* Lifecycle/device/layout guards: release warm stream on minimize/restore, rotate, headset changes */
  useEffect(() => {
    const releaseWarm = (reason) => {
      if (recorderRef.current) return; // don't break an active recording
      if (!streamRef.current) return;
      log(`🧯 Releasing warm mic stream (${reason})`);
      clearTimeout(keepAliveRef.current);
      try { streamRef.current.getTracks().forEach(t => t.stop()); } catch {}
      streamRef.current = null;
    };

    const onVisibility = () => {
      // iOS/Safari/PWA can resume with stale routes; force fresh stream next time
      // Release on both hidden and visible so the resume path always reacquires cleanly
      releaseWarm(`visibility:${document.visibilityState}`);
    };
    const onPageHide  = () => releaseWarm('pagehide');
    const onPageShow  = () => releaseWarm('pageshow'); // BFCache restore
    const onResize    = () => releaseWarm('resize');
    const onOrient    = () => releaseWarm('orientationchange');
    const onDevChange = () => releaseWarm('mediaDevices.devicechange');

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onOrient);
    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', onDevChange);
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onOrient);
      if (navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', onDevChange);
      }
    };
  }, []);


  /* helpers */
  function safeRelease() {
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
    streamRef.current = null;
  }

  async function withMicSession(fn) {
    const hasAS = typeof navigator !== 'undefined' && 'audioSession' in navigator;
    try {
      if (hasAS) {
        try { navigator.audioSession.type = 'auto'; } catch {}
        try { navigator.audioSession.type = 'play-and-record'; } catch {}
      }
      return await fn();
    } finally {
      if (hasAS) {
        try { navigator.audioSession.type = 'playback'; } catch {}
        try { navigator.audioSession.type = 'auto'; } catch {}
      }
    }
  }

  /* 1) start */
  const start = async () => {
    if (!supported || recording || starting) return;

    setStarting(true);
    setRecording(true); // Set recording immediately for UI feedback
    log('⏺️ START pressed');

    let stream;
    try {
      await withMicSession(async () => {
        // Always start clean; we keep it alive only after stop
        clearTimeout(keepAliveRef.current);
        safeRelease();
        log('🎙 acquiring mic stream…');
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            channelCount: 1
          }
        });
        streamRef.current = stream;
      });
    } catch (err) {
      console.error('getUserMedia failed', err);
      setRecording(false);
      setStarting(false);
      return;
    }

    recorderRef.current = new RecordRTC(stream, {
      type                 : 'audio',
      mimeType             : /*isIOS ? 'audio/wav' :*/ 'audio/webm',
      recorderType         : StereoAudioRecorder,
      numberOfAudioChannels: 1,
      //desiredSampRate      : 16000,
    });

    recorderRef.current.startRecording();
    startTimeRef.current = Date.now();
    setStarting(false);

    log('▶️ recorder started');
  };

  /* 2) stop */
  const stop = () => new Promise(res => {
    if (!recording || !recorderRef.current) return res(null);

    setRecording(false);
    log('⏹️ STOP pressed');

    const rec = recorderRef.current;
    rec.stopRecording(() => {
      const blob = rec.getBlob();
      rec.destroy();
      recorderRef.current = null;

      /* keep the stream cached for the next press */
      const dur = Date.now() - startTimeRef.current;
      log('🧹 cleaned, ⏱', dur, 'ms, 🎁', blob.size);

      res(dur >= MIN_DURATION_MS && blob.size ? blob : null);

      // Release the mic as soon as recording ends. Holding it open between
      // presses keeps the OS input device claimed and disrupts other apps
      // (Meet, Zoom) using the mic.
      clearTimeout(keepAliveRef.current);
      safeRelease();
    });
  });

  return { start, stop, recording, starting, supported };
}
